import { type OrchestratedQuote } from '@b-stock/fe-core/bstock-next/queries'
import { centsToDollars } from '@b-stock/fe-core/bstock-react'
import { ShipmentType } from '@b-stock/shipment-api-client'

// These shipment types are called "fixed shipping"
const fixedShipmentTypes = [
  ShipmentType.BuyerPickup,
  ShipmentType.BuyerArranged,
  ShipmentType.Free,
  ShipmentType.FlatRate,
]

export const isFixedShipmentType = (x: ShipmentType | null) =>
  x && fixedShipmentTypes.includes(x)

export const isGeofencedFlatRateQuote = (
  quote: OrchestratedQuote | null | undefined
) => {
  const isFlatRateQuote = quote?.shipmentType === ShipmentType.FlatRate
  return isFlatRateQuote && quote.isGeofencedRate === true
}

const getShippingRateType = (
  selectedQuote?: OrchestratedQuote | null
): 'flat' | 'quoted' | null => {
  if (selectedQuote?.shipmentType == null) return null
  return selectedQuote.shipmentType === ShipmentType.FlatRate
    ? 'flat'
    : 'quoted'
}

/**
 * Geofence properties shared by `confirm_max_bid` and `confirm_buy_now`.
 * Always returns all props so non-geofenced listings report
 * false/null/actual type rather than omitting the fields.
 */
export const buildGeofenceBidAnalyticsProperties = ({
  selectedQuote,
  anyAddressWithinRadius,
}: {
  selectedQuote?: OrchestratedQuote | null
  anyAddressWithinRadius: boolean
}) => ({
  // "Geofenced flat rate applied" — not raw destination eligibility.
  geofence_listing_address_within_radius:
    isGeofencedFlatRateQuote(selectedQuote),
  geofence_listing_any_address_within_radius: anyAddressWithinRadius,
  shipment_type: selectedQuote?.shipmentType ?? null,
  shipping_rate_type: getShippingRateType(selectedQuote),
  flat_rate_amount_usd:
    selectedQuote?.shipmentType === ShipmentType.FlatRate
      ? centsToDollars(selectedQuote.totalPrice)
      : null,
  address_id: selectedQuote?.destination.id ?? null,
})

// Rate-type transition, or null when a side is unknown.
const buildRateTypeChange = (
  fromShipmentType: ShipmentType | null,
  toShipmentType: ShipmentType | null | undefined
): string | null => {
  if (fromShipmentType == null || toShipmentType == null) return null
  if (fromShipmentType === toShipmentType) return 'no_change'
  return `${fromShipmentType}_to_${toShipmentType}`
}

/**
 * Geofence address-switch properties for `confirm_shipping_selection`.
 * Pure: within-radius booleans are inputs, not computed here. Always returns
 * all five props so non-geofenced listings report false/null uniformly.
 */
export const buildGeofenceShippingSelectionProperties = ({
  selectedQuote,
  toShipmentType,
  toAddressId,
  fromWithinRadius,
  toWithinRadius,
}: {
  selectedQuote?: OrchestratedQuote | null
  toShipmentType?: ShipmentType | null
  toAddressId?: string
  fromWithinRadius: boolean
  toWithinRadius: boolean
}) => {
  const fromShipmentType = selectedQuote?.shipmentType ?? null

  return {
    geofence_listing_from_address_within_radius: fromWithinRadius,
    geofence_listing_to_address_within_radius: toWithinRadius,
    is_address_changed: selectedQuote?.destination.id !== toAddressId,
    flat_rate_shown: toShipmentType === ShipmentType.FlatRate,
    // No destination address (pickup branch) → no meaningful transition.
    rate_type_change: toAddressId
      ? buildRateTypeChange(fromShipmentType, toShipmentType)
      : null,
  }
}

/**
 * Return URL for `editDeliveryMethod({ redirectURI })` — path+search.
 */
export function getEditDeliveryMethodRedirectUri(): string {
  if (typeof window === 'undefined') return ''
  const { pathname, search } = window.location
  return `${pathname}${search}`
}
